/**
 * Les commandes issues d'un plan (V1.6-09).
 *
 * Cet écran répond à « qu'est-ce qu'on a commandé, et où ça en est ». Il ne
 * commande rien lui-même : un bon naît sur la fiche d'un plan approuvé, et
 * quelqu'un vient ensuite dire qu'il l'a réellement passé chez le fournisseur.
 *
 * **La progression ligne par ligne n'est pas dans cette liste, et c'est
 * délibéré.** La dire pour chaque bon demanderait de sommer toutes les
 * réceptions de tous les bons ; sur cinquante commandes, ce serait payer très
 * cher un chiffre que la fiche donne exactement. Le statut, lui, est déjà
 * calculé côté serveur depuis ce qui est arrivé — c'est lui qui distingue une
 * commande en attente d'une commande à moitié rentrée.
 */
import { Link, useSearchParams } from "react-router-dom";
import { ShoppingCart } from "lucide-react";

import { usePlannedPurchaseOrders } from "@/api/purchase-orders";
import type { PlannedPurchaseOrder } from "@/api/types";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatAmount } from "@/lib/comparison-display";
import { formatDate, formatPurchaseOrderStatus, pluralize } from "@/lib/format";
import { purchaseOrderTone } from "@/lib/stock";

const PAGE_SIZE = 50;

/**
 * Le total d'un bon, ou la raison de son absence.
 *
 * `null` ne veut pas dire zéro : il veut dire que le fournisseur n'a pas
 * chiffré son transport. Un total partiel présenté comme complet finirait dans
 * un budget.
 */
function OrderTotal({ order }: { order: PlannedPurchaseOrder }) {
  if (order.totals.total === null) {
    return <span className="text-ink-subtle">Transport inconnu</span>;
  }
  return <>{formatAmount(order.totals.total, order.totals.currency)}</>;
}

export function PlannedOrdersPage() {
  const [params, setParams] = useSearchParams();
  const offset = Number(params.get("offset") ?? 0);

  const { data, isLoading, error, refetch } = usePlannedPurchaseOrders({
    limit: PAGE_SIZE,
    offset,
  });

  function goTo(next: number) {
    const merged = new URLSearchParams(params);
    if (next === 0) merged.delete("offset");
    else merged.set("offset", String(next));
    setParams(merged, { replace: true });
  }

  return (
    <Page>
      <PageHeader
        title="Commandes"
        subtitle="Ce qui a été commandé à un fournisseur, et ce qui en est arrivé. Créer un bon ne le passe pas ; recevoir n'est pas finaliser."
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={6} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={<ShoppingCart className="size-6" aria-hidden />}
              title="Aucune commande"
              description="Une commande se crée depuis la fiche d'un plan d'achat approuvé, un bon par soumission retenue."
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="w-32 whitespace-nowrap">Bon</Th>
                      <Th className="max-w-0">Fournisseur</Th>
                      <Th>Statut</Th>
                      <Th align="right" className="hidden whitespace-nowrap @3xl:table-cell">
                        Lignes
                      </Th>
                      <Th align="right" className="whitespace-nowrap">
                        Total
                      </Th>
                      <Th className="hidden whitespace-nowrap @4xl:table-cell">Commandé le</Th>
                      <Th className="hidden whitespace-nowrap @5xl:table-cell">Attendu</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((order) => (
                      <OrderRow key={order.id} order={order} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((order) => (
                  <li key={order.id}>
                    <OrderCard order={order} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={goTo}
          />
        )}
      </div>
    </Page>
  );
}

function OrderRow({ order }: { order: PlannedPurchaseOrder }) {
  return (
    <Tr>
      <Td className="whitespace-nowrap font-mono text-[12px] text-ink-muted">
        <Link to={`/commandes/${order.id}`} className="hover:text-brand">
          {order.code}
        </Link>
      </Td>
      <Td className="max-w-0">
        <Link
          to={`/commandes/${order.id}`}
          className="block truncate font-medium text-ink hover:text-brand"
        >
          {order.supplier_name}
        </Link>
        {order.quote_code && (
          <span className="block truncate font-mono text-[11px] text-ink-subtle">
            {order.quote_code}
            {order.quote_revision && order.quote_revision > 1 ? ` rév. ${order.quote_revision}` : ""}
          </span>
        )}
      </Td>
      <Td>
        <StatusBadge tone={purchaseOrderTone(order.status)}>
          {formatPurchaseOrderStatus(order.status)}
        </StatusBadge>
      </Td>
      <Td align="right" className="tnum hidden text-[13px] text-ink-muted @3xl:table-cell">
        {order.line_count}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
        <OrderTotal order={order} />
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-muted @4xl:table-cell">
        {order.ordered_on ? formatDate(order.ordered_on) : "—"}
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-muted @5xl:table-cell">
        {order.expected_on ? formatDate(order.expected_on) : "Date inconnue"}
      </Td>
    </Tr>
  );
}

function OrderCard({ order }: { order: PlannedPurchaseOrder }) {
  return (
    <Link
      to={`/commandes/${order.id}`}
      className="flex items-start gap-3 px-3 py-3 active:bg-surface-hover"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{order.supplier_name}</p>
        <p className="truncate font-mono text-[11px] text-ink-subtle">{order.code}</p>
        <p className="tnum mt-0.5 text-[12px] text-ink-muted">
          <OrderTotal order={order} />
        </p>
        <p className="mt-0.5 truncate text-[11px] text-ink-subtle">
          {pluralize(order.line_count, "ligne", "lignes")} ·{" "}
          {order.expected_on ? `attendu ${formatDate(order.expected_on)}` : "date inconnue"}
        </p>
      </div>
      <StatusBadge tone={purchaseOrderTone(order.status)} className="shrink-0">
        {formatPurchaseOrderStatus(order.status)}
      </StatusBadge>
    </Link>
  );
}
